import React, { useRef, useEffect } from 'react';
import {
  PaperClipIcon,
  PaperAirplaneIcon,
  UserCircleIcon,
  ChatBubbleLeftRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  ChatWindowProps,
  Message,
  Attachment,
} from '../types/Messaging';
import {
  formatTimestamp,
  groupMessagesByDate,
  formatDateHeader,
  formatRelativeTime,
} from '../utils/dateFormatting';

const CHAT_HEADER_HEIGHT = 56;
const ALLOWED_FILE_TYPES = [
  "pdf", "doc", "docx", "xls", "xlsx", "jpg", "jpeg", "png", "webp",
];
const ACCEPT_STRING = ALLOWED_FILE_TYPES.map(ext => `.${ext}`).join(",");

const ChatWindow: React.FC<ChatWindowProps & {
  errorMessage?: string;
  setErrorMessage?: (msg: string | null) => void;
}> = ({
  selectedConv,
  messages,
  messagesLoading,
  currentUserId,
  newMessage,
  setNewMessage,
  handleSendMessage,
  handleFileUpload,
  uploadedFiles,
  setUploadedFiles,
  isMobile,
  showHeader = true,
  onBack,
  errorMessage,
  setErrorMessage,
}) => {
    const messagesEndRef = useRef<HTMLDivElement | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    useEffect(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }, [messages.length]);

    return (
      <div className="flex flex-col h-full w-full">
        {selectedConv ? (
          <>
            {showHeader === true && (
              <div
                className="flex items-center px-6 border-b border-gray-200 bg-white font-semibold text-gray-900 text-lg"
                style={{ minHeight: CHAT_HEADER_HEIGHT, height: CHAT_HEADER_HEIGHT }}
              >
                {onBack && (
                  <button
                    onClick={onBack}
                    className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors mr-2"
                    aria-label="Back to conversations"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <div className="w-8 h-8 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center mr-3">
                  <UserCircleIcon className="w-5 h-5 text-gray-600" />
                </div>
                <span className="text-base font-semibold text-gray-900 truncate">
                  {(selectedConv.otherParticipantName && selectedConv.otherParticipantName !== "Unknown Concierge")
                    ? selectedConv.otherParticipantName
                    : "Unassigned Concierge"}
                </span>
              </div>
            )}

            {/* Messages */}
            <div
              className="flex-1 flex flex-col overflow-y-auto px-4 py-2 bg-white"
              style={{ minHeight: 0 }}
            >
              {messagesLoading ? (
                <div className="flex justify-center items-center h-32 text-gray-500">Loading messages...</div>
              ) : messages.length === 0 ? (
                <div className="flex justify-center items-center h-32 text-gray-500">No messages yet</div>
              ) : (
                (() => {
                  const groupedMessages = groupMessagesByDate(messages);
                  const sortedDates = Object.keys(groupedMessages).sort(
                    (a, b) => new Date(a).getTime() - new Date(b).getTime()
                  );
                  return sortedDates.map(dateKey => (
                    <div key={dateKey}>
                      {/* Date Header */}
                      <div className="flex justify-center my-4">
                        <div className="dag-msg-daysep text-gray-500 text-xs flex items-center space-x-1">
                          <span>{formatDateHeader(groupedMessages[dateKey][0].sentDate).text}</span>
                          <span>•</span>
                          <span>{formatDateHeader(groupedMessages[dateKey][0].sentDate).date}</span>
                        </div>
                      </div>

                      {groupedMessages[dateKey].map((message: Message) => {
                        const isCurrentUser = message.senderId === currentUserId;
                        return (
                          <div
                            key={message.id}
                            className={`flex items-end space-x-2 mb-4 ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                          >
                            {!isCurrentUser && (
                              <div className="w-8 h-8 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                                <UserCircleIcon className="w-5 h-5 text-gray-600" />
                              </div>
                            )}

                            <div className={`flex flex-col max-w-[75%] ${isCurrentUser ? 'items-end' : 'items-start'}`}>
                              <div className={`dag-bubble rounded-2xl px-4 py-2 ${isCurrentUser ? 'dag-bubble--out dfo-bg-gradient-teal-horizontal text-white rounded-br-md' : 'dag-bubble--in bg-gray-100 text-gray-900 rounded-bl-md'}`}>
                                {/* Only render <p> if content exists */}
                                {message.content && (
                                  <p className="text-sm leading-relaxed break-all">{message.content}</p>
                                )}

                                {/* Always render attachments if any */}
                                {message.attachments && message.attachments.length > 0 && (
                                  <div className="mt-2 space-y-2">
                                    {message.attachments.map((attachment: Attachment) => (
                                      <div
                                        key={attachment.id}
                                        className={`p-2 rounded-lg border ${isCurrentUser ? 'bg-teal-700 border-teal-800' : 'bg-white border-gray-300'}`}
                                      >
                                        <div className="flex items-center space-x-2">
                                          <PaperClipIcon className="w-4 h-4" />
                                          <div className="flex-1 min-w-0">
                                            <p className={`text-xs truncate ${isCurrentUser ? 'text-teal-100' : 'text-gray-700'}`}>
                                              {attachment.originalFileName || attachment.fileName}
                                            </p>
                                            <p className={`text-xs ${isCurrentUser ? 'text-teal-200' : 'text-gray-500'}`} title={formatTimestamp(attachment.uploadedAt)}>
                                              {formatRelativeTime(attachment.uploadedAt)}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              <p className={`text-xs text-gray-500 mt-1 ${isCurrentUser ? 'text-right mr-1' : 'text-left ml-1'}`} title={formatTimestamp(message.sentDate)}>
                                {formatRelativeTime(message.sentDate)}
                              </p>
                            </div>

                            {isCurrentUser && (
                              <div className="w-8 h-8 dfo-bg-gradient-teal-horizontal rounded-full flex items-center justify-center flex-shrink-0">
                                <UserCircleIcon className="w-5 h-5 text-white" />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ));
                })()
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <div
              className="border-t border-gray-200 bg-white flex-shrink-0 relative"
              style={{
                minHeight: 96,
                maxHeight: 96,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
              }}
            >
              {/* Error message display */}
              {errorMessage && (
                <div className="px-4 py-2 bg-red-50 border border-red-200 rounded text-red-700 mb-2 text-sm flex items-center">
                  <span>{errorMessage}</span>
                  <button className="ml-auto text-red-500 hover:text-red-800" onClick={() => setErrorMessage?.(null)}>×</button>
                </div>
              )}

              {/* Uploaded files preview */}
              {uploadedFiles.length > 0 && (
                <div className="px-4 py-1 bg-green-50 border border-green-200 rounded-t-lg">
                  <div className="flex items-center space-x-2 text-xs text-green-700">
                    <PaperClipIcon className="w-3 h-3" />
                    <span>
                      {uploadedFiles.length} file
                      {uploadedFiles.length > 1 ? 's' : ''}
                    </span>
                    <div className="flex space-x-1 flex-wrap">
                      {uploadedFiles.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center space-x-1 bg-green-100 px-1.5 py-0.5 rounded text-xs max-w-32"
                        >
                          <span className="truncate">
                            {file.name}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setUploadedFiles(prev => prev.filter((_, i) => i !== index));
                            }}
                            className="text-green-600 hover:text-green-800 hover:bg-green-200 rounded-full w-4 h-4 flex items-center justify-center"
                            title={`Remove ${file.name}`}
                          >
                            <XMarkIcon className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              <div className="p-4 pt-2 bg-white">
                <form
                  onSubmit={handleSendMessage}
                  className="flex items-center space-x-3"
                  autoComplete="off"
                >
                  <div className="flex-1">
                    <textarea
                      ref={textareaRef}
                      value={newMessage}
                      onChange={(e) => {
                        setNewMessage(e.target.value);
                        setErrorMessage?.(null);
                      }}
                      placeholder={isMobile ? 'Type here...' : 'Type your message...'}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-green-500 resize-none min-h-[40px] max-h-[80px] overflow-y-auto"
                      rows={1}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage(e);
                        }
                      }}
                    />
                  </div>

                  {/* File Upload */}
                  <label className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 cursor-pointer transition-colors mb-0">
                    <PaperClipIcon className="w-5 h-5 text-gray-600" />
                    <input type="file" multiple className="hidden" accept={ACCEPT_STRING} onChange={handleFileUpload} />
                  </label>

                  {/* Send Button */}
                  <button type="submit" disabled={!newMessage.trim() && uploadedFiles.length === 0} className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-[var(--btn-primary-bg)] hover:bg-[var(--btn-primary-bg-hover)] disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors">
                    <PaperAirplaneIcon className="w-5 h-5 text-white" />
                  </button>
                </form>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center h-full w-full min-h-0">
            <div className="text-center opacity-80">
              <ChatBubbleLeftRightIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Select a conversation</h3>
              <p className="text-gray-600">Choose a conversation from the sidebar to start messaging</p>
            </div>
          </div>
        )}
      </div>
    );
  };

export default ChatWindow;
